// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngLoadingOverlay from "@rc/App/Components/Common/XngLoadingOverlay";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { getSettingsCollection } from "@rc/App/Store/Selectors/SettingsSelectors";
import { screen } from "@rc/XDL/Helpers";
import { getScripts } from "@rc/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;
    const location = getLocation(state);

    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isHeaderPage: pageType === "Header", // HeaderPage
        searchInputFocus: state.xHeader.searchInputFocus,
        showDropShadow: state.xHeader.showDropShadow,
        location,
        isCheckoutPages:
            pageType === "CheckoutShippingPage" ||
            pageType === "CheckoutReviewAndSubmitPage" ||
            pageType === "OrderConfirmationPage" ||
            pageType === "RfqRequestQuotePage" ||
            pageType === "RfqConfirmationPage",
        isChangeCustomerPage: pageType === "ChangeCustomerPage",
        bvScriptUrl: getSettingsCollection(state).customSetting?.rcBazaarVoiceJavascriptUrl,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

export const HeaderSize: any = {
    default: 110,
    // sticky: 94,
    mobile: 60,
    top: 0,
    checkoutHeader: 68,
    checkoutMobileHeader: 60,
    changeCustomerHeader: 40,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    header?: ContainerProps;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerContentWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    mainNavigationPage?: ContainerProps;
    logoLinksSearchPage?: ContainerProps;
    placeholder?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: fixed !important;
            top: 0;
            left: 0;
            right: 0;
            z-index: 1286;
            background: ${getColor("common.white")} !important;
            height: ${HeaderSize.default}px;
            border-bottom: 1px solid ${getColor("common.ui3")};

            ${screen("lte", "md")`
                height: ${HeaderSize.mobile}px;
            `}
            // Preventing header from appearing on each page in print view
            @media print {
                position: relative !important;
            }
            ${({ isCheckoutPages }: any) => {
                if (isCheckoutPages) {
                    return `
                    height: ${HeaderSize.checkoutHeader}px;
                    overflow: hidden;

                    .quick-order-item-wrapper {
                        display: none !important;
                        height: 0 !important;
                        max-width: 0 !important;
                    }

                    @media (max-width: 1024px) {
                        height: ${HeaderSize.checkoutMobileHeader}px;
                    }
                    `;
                }
                return `

                `;
            }}
            ${({ isChangeCustomerPage }: any) => {
                if (isChangeCustomerPage) {
                    return `
                        dispaly: none !important;
                        visibility: hidden;
                        height: 0;
                        max-height: 0;
                        pointer-events: none;
                    `;
                }
                return `

                `;
            }}

            @media print {
                display: block !important;
            }

            &:after {
                transition: 1000ms all;
                content: " ";
                position: fixed;
                top: 110px;
                bottom: 0;
                left: 0;
                right: 0;
                z-index: 10;
                visibility: hidden;
                background: transparent;
                @media (max-width: 1024px) {
                    top: 60px;
                }
                ${({ showDropShadow }: any) =>
                    showDropShadow
                        ? `
                    background: rgba(0, 0, 0, 0.5);
                    visibility: visible;
                `
                        : ""}
            }
        `,
    },
    headerContentWrapper: {
        css: css`
            @media print {
                position: static;
            }
        `,
    },
    logoLinksSearchWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            background-color: transparent;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },

    logoLinksSearchPage: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = headerStyles;
const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

let addedBvScript = false;
const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isHeaderPage,
    searchInputFocus,
    location,
    isCheckoutPages,
    showDropShadow,
    isChangeCustomerPage,
    bvScriptUrl,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const fireDatalayerLoadedEvent = () => {
        const customGA4ScriptId = siteMessage("Custom_GA4_ScriptId") as any;
        const customGA4ScriptEnvironment = siteMessage("Custom_GA4_ScriptEnvironment") as any;
        const customGA4ScriptCountry = siteMessage("Custom_GA4_ScriptCountry") as any;

        const shouldShowDataLayerScript =
            customGA4ScriptId?.length &&
            customGA4ScriptId !== "Custom_GA4_ScriptId" &&
            customGA4ScriptEnvironment?.length &&
            customGA4ScriptEnvironment !== "Custom_GA4_ScriptEnvironment" &&
            customGA4ScriptCountry?.length &&
            customGA4ScriptCountry !== "Custom_GA4_ScriptCountry";

        if (shouldShowDataLayerScript) {
            const script = document.createElement("script");
            script.innerHTML = `
                var dataLayer = window.dataLayer;
                dataLayer.push({
                event: "datalayerLoaded",
                site: {
                "id": "${siteMessage("Custom_GA4_ScriptId") as string}", // constant
                "environment": "${siteMessage("Custom_GA4_ScriptEnvironment") as string}", // constant
                "country": "${siteMessage("Custom_GA4_ScriptCountry") as string}" // constant (ISO2)
                }
                });
            `;
            document.head.appendChild(script);
        }
    };

    const updateOgMetaDescription = () => {
        const ogDescriptionMeta = document.querySelector('meta[name="og:description"]');
        if (ogDescriptionMeta) {
            ogDescriptionMeta.parentNode?.removeChild(ogDescriptionMeta);
        }

        const metaDescription = document.querySelector('meta[name="description"]');

        if (metaDescription) {
            const descriptionContent = metaDescription.getAttribute("content");

            const meta = document.createElement("meta");
            meta.setAttribute("property", "og:description");
            meta.setAttribute("content", descriptionContent || "");
            document.head.appendChild(meta);
        }
    };

    useEffect(() => {
        if (bvScriptUrl && !addedBvScript) {
            getScripts.url(bvScriptUrl, "optanon-category-2", "text/plain");
            addedBvScript = true;
        }
    }, [bvScriptUrl]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        fireDatalayerLoadedEvent();
    }, []);

    useEffect(() => {
        updateOgMetaDescription();
    }, [location]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <Page
            {...styles.header}
            id="header"
            {...({ isHeaderPage, isCheckoutPages, showDropShadow, isChangeCustomerPage } as any)}
        >
            <SkipNav
                extendedStyles={styles.skipToContent}
                text={translate("Skip to main content")}
                destination={afterHeader}
            />
            <StyledWrapperX
                {...styles.headerContentWrapper}
                isHeaderPage={isHeaderPage}
                className="header_content-wrapper"
            >
                <StyledWrapper
                    {...styles.logoLinksSearchWrapper}
                    {...({ isHeaderPage } as any)}
                    className="main-navigation-wrapper"
                >
                    <Page {...styles.logoLinksSearchPage}>
                        <Zone contentId={id} zoneName="LogoLinksSearch" />
                    </Page>
                </StyledWrapper>
                {!isCheckoutPages && (
                    <StyledWrapper
                        {...styles.mainNavigationWrapper}
                        {...({ isHeaderPage } as any)}
                        className="main-navigation-wrapper"
                    >
                        <Page {...styles.mainNavigationPage}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </Page>
                    </StyledWrapper>
                )}
            </StyledWrapperX>

            <span ref={afterHeader} tabIndex={-1} />
            <XngLoadingOverlay />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
